import type { Thread, ThreadBump } from "@talkers/shared";
import { createRoot } from "solid-js";
import { createStore, produce, reconcile } from "solid-js/store";
import { api } from "@/api/client";
import { isAdmin } from "@/state/me";

/**
 * スレッド一覧のキャッシュ。
 *
 * ページごとに取り直すと、カテゴリを行き来するだけで毎回 DB を叩くことになる。
 * 一度読んだ一覧はここに残し、以後の更新は WebSocket のイベントで当てる
 * (`applyBump` / `applyCreated`)。
 *
 * 一覧はカテゴリごとに分けて持つ。同じスレッドが複数の一覧に載るので、
 * 実体は `threads` に 1 つだけ置いて id で参照する。
 */

/**
 * 一覧のキー。管理者モードを混ぜてある。
 *
 * 見えるスレッドがモードで変わるので、切り替えたら別の一覧として取り直す必要がある。
 * キーに入れておけば、読み込み済みかを見るだけで自動的にそうなる
 * (`me` の解決前に読んだ一覧も、admin と分かった時点で別扱いになる)。
 */
const listKey = (category: string | undefined): string =>
  `${isAdmin() ? "admin" : "all"}:${category ?? ""}`;

export const {
  threads,
  threadIds,
  loadThreadList,
  isListLoaded,
  applyBump,
  applyCreated,
  applyVisibility,
  invalidate,
} = createRoot(() => {
  const [threads, setThreads] = createStore<Record<number, Thread>>({});
  const [lists, setLists] = createStore<Record<string, number[]>>({});

  /** 同じ一覧を同時に取りに行かないための重複除け。 */
  const inflight = new Map<string, Promise<void>>();

  const put = (list: Thread[]): void => {
    setThreads(
      produce((current) => {
        for (const thread of list) current[thread.id] = thread;
      }),
    );
  };

  /** 新しい順。`lastPostAt` が無ければ作成時刻で比べる。 */
  const sortKey = (id: number): string => {
    const thread = threads[id];
    return thread === undefined ? "" : (thread.lastPostAt ?? thread.createdAt);
  };

  const resort = (key: string): void => {
    setLists(key, (ids) => [...ids].sort((a, b) => sortKey(b).localeCompare(sortKey(a))));
  };

  return {
    threads,
    threadIds: (category?: string) => lists[listKey(category)],
    isListLoaded: (category?: string) => lists[listKey(category)] !== undefined,

    /** 未取得のときだけ読む。読み直したいときは `invalidate` してから呼ぶ。 */
    async loadThreadList(category?: string): Promise<void> {
      const key = listKey(category);
      if (lists[key] !== undefined) return;

      const running = inflight.get(key);
      if (running !== undefined) return await running;

      const task = (async () => {
        const page = await api.listThreads({
          ...(category === undefined ? {} : { category }),
          // admin でなければサーバが無視する
          ...(isAdmin() ? { includeHidden: true } : {}),
        });
        put(page.data);
        setLists(
          key,
          page.data.map((thread) => thread.id),
        );
      })().finally(() => inflight.delete(key));

      inflight.set(key, task);
      return await task;
    },

    /** レスが付いた。件数と時刻を当てて、載っている一覧を並べ直す。 */
    applyBump(bump: ThreadBump): void {
      if (threads[bump.threadId] === undefined) return;
      setThreads(bump.threadId, {
        postCount: bump.postCount,
        lastPostAt: bump.lastPostAt,
      });
      for (const key of Object.keys(lists)) {
        if (lists[key]?.includes(bump.threadId) === true) resort(key);
      }
    },

    /**
     * スレッドが立った。読み込み済みの一覧にだけ足す。
     * 未取得の一覧に足すと、その一覧を「読み込み済み」と誤認してしまう。
     */
    applyCreated(thread: Thread): void {
      put([thread]);
      for (const key of [listKey(undefined), listKey(thread.category.slug)]) {
        const ids = lists[key];
        if (ids === undefined || ids.includes(thread.id)) continue;
        setLists(key, [thread.id, ...ids]);
      }
    },

    /**
     * 表示 / 非表示が切り替わった。
     * 管理者モードなら印を付けるだけ、そうでなければ一覧から出し入れする。
     */
    applyVisibility(thread: Thread): void {
      put([thread]);
      const keys = [listKey(undefined), listKey(thread.category.slug)];
      for (const key of keys) {
        const ids = lists[key];
        if (ids === undefined) continue;
        const listed = ids.includes(thread.id);
        // 管理者モードでは伏せたものも並べたままにする (印で区別する)
        const shouldList = isAdmin() || !thread.isHidden;
        if (shouldList && !listed) {
          setLists(key, [thread.id, ...ids]);
          resort(key);
        }
        if (!shouldList && listed) {
          setLists(
            key,
            ids.filter((id) => id !== thread.id),
          );
        }
      }
    },

    /**
     * 一覧を捨てて取り直させる。WebSocket が切れていた間の更新は届いていないので、
     * 再接続したときに呼ぶ。
     */
    invalidate(): void {
      setLists(reconcile({}));
    },
  };
});
